// Instagram API
import { token, getAPI } from "@/api/params.js";
import { parseJsonResponse, makeJsonRequest } from "@/api/common.js";
import { getTimestampedUrl } from "../common";

/**
 * Return list of Instagram pages binded to Facebook account
 * @param {Number} facebookAccId Facebook internal account ID
 * @returns {Array} list of Instagram accounts
 */
export const getInstagramPages = async facebookAccId => {
  const url = `${getAPI()}/user/socials/facebook/${facebookAccId}/instagram_business_account?token=${token}`;
  const resp = await fetch(url);
  let respObj = await parseJsonResponse(resp);

  /** it always should be an array of accounts data */
  if (!Array.isArray(respObj)) {
    respObj = [respObj];
  }

  const accountsInfo = respObj.map(obj =>
    parseInstagramPage(obj["instagram_business_account"], facebookAccId, obj.id)
  );
  return accountsInfo;
};

/**
 * Share new video to Instagram
 * @param {Number} videoId Video ID
 * @param {String} accountId Instagram social account ID
 * @param {Number} facebookAccountId Facebook internal account ID
 * @param {String} facebookPageId Facebook social page ID
 * @param {Number} caption Description text
 */
export const shareToInstagram = async (
  videoId,
  accountId,
  facebookAccountId,
  facebookPageId,
  metadata,
  multistepId
) => {
  const url =
    multistepId === undefined
      ? getTimestampedUrl(
          `${getAPI()}/video/${videoId}/share/instagram?social=${facebookAccountId}&igUserId=${accountId}&igPageId=${facebookPageId}&token=${token}`,
          metadata.scheduledTime
        )
      : getTimestampedUrl(
          `${getAPI()}/video/${videoId}/share/instagram?social=${facebookAccountId}&igUserId=${accountId}&igPageId=${facebookPageId}&token=${token}&multistepId=${multistepId}`,
          metadata.scheduledTime,
          multistepId
        );
  const data = {
    caption: metadata.caption
  };

  await makeJsonRequest({
    url,
    method: "POST",
    data: data
  });
};

/**
 * Number of instagram Sharings
 * @param {String} pageId Instagram social page ID
 * @param {Number} igUserId User ID
 */
export const getInstagramQuota = async (pageId, igUserId) => {
  const publushLimitUrl = `${getAPI()}/video/0/share/instagram/publish_limit?token=${token}&igUserId=${igUserId}&igPageId=${pageId}`;
  const quota_total = await fetch(publushLimitUrl);
  const { data, success, error } = await quota_total.json();

  if (!success) {
    throw new Error(error);
  }

  const total = data.config.quota_total;
  const usage = data.quota_usage;
  return { total, usage };
};

/**
 * Convert Instagram account object from server-format to client-format
 * @param {Object} object server-format data object
 * @param {Number} accountId Facebook internal account ID
 * @param {String} pageId Facebook social page ID
 * @returns {Object} Parsed object
 */
function parseInstagramPage(object, accountId, pageId) {
  return {
    id: object.id,
    label: object.name,
    social: "instagram",
    metadata: {
      facebookAccountId: accountId,
      facebookPageId: pageId
    }
  };
}
